import React, { useMemo, useState } from 'react';
import {
  Button,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { ExpandMore, PieChart } from '@mui/icons-material';

export const SUMMARY_TYPE = {
  BUSINESS: 'business',
  POTENTIAL: 'potential',
  SYNCHRONIZER: 'synchronizer',
};

export const DEFAULT_SUMMARY_OPTIONS = [
  { value: SUMMARY_TYPE.BUSINESS, label: 'Business Area Summary' },
  { value: SUMMARY_TYPE.POTENTIAL, label: 'Potential Label Summary' },
  { value: SUMMARY_TYPE.SYNCHRONIZER, label: 'Synchronizer Summary' },
];

const getEnabledOptions = options => (options || DEFAULT_SUMMARY_OPTIONS).filter(option => !option.hidden);

const SummarySelectorButton = ({
  options = DEFAULT_SUMMARY_OPTIONS,
  onOpen,
  buttonLabel = 'Summaries',
  tooltip = 'View Summaries',
  iconOnly = false,
  disabled = false,
  loading = false,
  size = 'medium',
  variant = 'contained',
  color = 'primary',
  fullWidth = false,
  buttonSx,
  menuId = 'summary-selector-menu',
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const enabledOptions = useMemo(() => getEnabledOptions(options), [options]);
  const enabledValues = useMemo(
    () => enabledOptions.filter(option => !option.disabled).map(option => option.value),
    [enabledOptions]
  );
  const [selectedValues, setSelectedValues] = useState(() => enabledValues);
  const [hasSelectionChanged, setHasSelectionChanged] = useState(false);
  const open = Boolean(anchorEl);

  const activeSelectedValues = (hasSelectionChanged ? selectedValues : enabledValues).filter(value =>
    enabledValues.includes(value)
  );
  const allSelected = enabledValues.length > 0 && enabledValues.every(value => activeSelectedValues.includes(value));
  const canOpenSummary = activeSelectedValues.length > 0;

  const openMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const toggleAll = () => {
    setHasSelectionChanged(true);
    setSelectedValues(allSelected ? [] : enabledValues);
  };

  const toggleValue = value => {
    setHasSelectionChanged(true);
    setSelectedValues(prevSelected =>
      prevSelected.includes(value)
        ? prevSelected.filter(selectedValue => selectedValue !== value)
        : [...prevSelected, value]
    );
  };

  const handleOpenSelected = () => {
    if (!canOpenSummary) return;
    closeMenu();
    onOpen?.(activeSelectedValues);
  };

  const trigger = iconOnly ? (
    <Tooltip title={tooltip}>
      <span>
        <IconButton
          size={size}
          onClick={openMenu}
          disabled={disabled}
          aria-controls={open ? menuId : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          sx={buttonSx}
        >
          {loading ? <CircularProgress size={18} /> : <PieChart fontSize='small' color='primary' />}
        </IconButton>
      </span>
    </Tooltip>
  ) : (
    <Button
      size={size}
      variant={variant}
      color={color}
      fullWidth={fullWidth}
      onClick={openMenu}
      disabled={disabled}
      startIcon={loading ? <CircularProgress size={16} color='inherit' /> : <PieChart />}
      endIcon={<ExpandMore sx={{ transform: `rotate(${open ? 180 : 0}deg)` }} />}
      aria-controls={open ? menuId : undefined}
      aria-haspopup='true'
      aria-expanded={open ? 'true' : undefined}
      sx={buttonSx}
    >
      {buttonLabel}
    </Button>
  );

  return (
    <>
      {trigger}
      <Menu id={menuId} anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <MenuItem onClick={toggleAll} disabled={!enabledValues.length}>
          <ListItemIcon>
            <Checkbox edge='start' size='small' checked={allSelected} tabIndex={-1} disableRipple />
          </ListItemIcon>
          <ListItemText primary='All' />
        </MenuItem>
        <Divider />
        {enabledOptions.map(option => {
          const checked = activeSelectedValues.includes(option.value);
          return (
            <MenuItem
              key={option.value}
              onClick={() => toggleValue(option.value)}
              disabled={option.disabled}
              dense={iconOnly}
            >
              <ListItemIcon>
                <Checkbox edge='start' size='small' checked={checked} tabIndex={-1} disableRipple />
              </ListItemIcon>
              <ListItemText primary={option.label} />
            </MenuItem>
          );
        })}
        <Divider />
        <Box sx={{ px: 2, py: 1.25 }}>
          <Button fullWidth variant='contained' size='small' onClick={handleOpenSelected} disabled={!canOpenSummary}>
            Open Summary
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default SummarySelectorButton;
